/**
 * PF v2 file hub — Wave-2 kinds: "Additional Information" cert uploads.
 * ============================================================================
 *
 * Both cert flows (consumption certificate + energy certificate of need) attach
 * supporting files to a product line through ONE resource — the v2 session-file
 * mint with `kind: DOCUMENT` and a per-scope `tag`. Four additive
 * plain-document kind descriptors, each reusing the shared transport (whose delete
 * FIXES E-1 by construction for all four — legacy cert delete passed only
 * `{sessionId}`, leaving the literal `{filename}` in the URL):
 *
 *   - `CERT_EXTERIOR_VIEW_KIND`     (`EXTERIOR_VIEW`)                    — both certs
 *   - `CERT_HEATER_NAMEPLATE_KIND`  (`HEATER_NAMEPLATE`)                 — both certs
 *   - `CERT_CONSTRUCTION_DESC_KIND` (`CONSTRUCTION_DESCRIPTION`)         — energy only
 *   - `CERT_BUILDING_SHAPE_KIND`    (`ENERGY_CERTIFICATE_BUILDING_SHAPE`)— energy only
 *
 * Scope strings are the legacy `AdditionalInformationUploadType` enum values (BE
 * keys on them) + the literal `'ENERGY_CERTIFICATE_BUILDING_SHAPE'` for #4. Each
 * is single-file and plain (`derivatives: []`, no derivative poll). The
 * `productCode` is per-upload-call — each cert module passes its own cart item's
 * code; the scope is shared / order-level.
 *
 * The scopes are STATIC strings (session read live inside the resolvers) and
 * `useFileAPI`'s `INIT_SCOPE` is destructive → exactly ONE consumer per scope.
 * The composed `PfV2AdditionalInfoUploadProvider` mounts all four provider owners
 * ONCE at the PF-v2 root (`PurchaseFlowRevamp.page`), so BOTH cert modules read
 * the SAME provider instances via the shared `use*Upload` hooks — a cart with
 * both a consumption AND an energy cert item shares one `useFileAPI(scope)` per
 * scope (no INIT_SCOPE collision; strictly better than legacy, where two cert
 * adapters each mounted their own provider on the same scope).
 *
 * A-04: these kinds are STORAGE-AGNOSTIC — they own transport + projection only.
 * Each cert MODULE owns its projection→DTO sync (interaction-gated + value-guarded,
 * §4b) for any upload that gates completeness.
 */

import { createElement, useCallback, useMemo, type FC, type ReactElement, type ReactNode } from 'react'

import { UploadTagType } from 'constants/documents'
import { SessionFileKind } from 'models/purchaseFlowRevamp'

import { createFileHubKind } from '../createFileHubKind'
import { makeSessionFileDeleteHandler, makeSessionFileUploadResolver } from '../sessionFiles.transport'
import type { FileHubBaseMetadata, FileHubKind } from '../fileHub.types'
import { projectSingleDoc, type DocProjection } from './docProjection'

// ─── Upload params ─────────────────────────────────────────────────────────

/**
 * Every cert scope needs the cart item's catalogue `productCode`, which the mint
 * takes verbatim. A `null` code rejects cleanly (chip → `FileState.ERROR`) instead
 * of sending a bad request. The scope is shared / order-level, so the code is
 * supplied per upload call.
 */
export interface AdditionalInfoUploadParams {
  productCode: string | null
  /** The line the file belongs to: two certificates in one cart share these scopes. */
  cartItemId: string
}

// ─── Kind factory ─────────────────────────────────────────────────────────

/**
 * A single-file plain-document cert kind: one FileAPI scope + the shared v2
 * session-file transport (`kind: DOCUMENT` plus the given `tag`), single-doc
 * projection.
 *
 * The v1 `documentType: INPUT` is gone from the body, not dropped: v2 derives the
 * resource type from `kind`, because a purchase-session upload is always a
 * client-supplied original and v1's full-enum parameter let a caller name a
 * derived type this path cannot legitimately produce.
 */
const docKind = (
  scope: string,
  tag: UploadTagType
): FileHubKind<FileHubBaseMetadata, DocProjection, AdditionalInfoUploadParams> => ({
  scope,
  derivatives: [],
  purgeOnDestroy: true,
  resolveTag: (params) => params.cartItemId,
  makeUploadResolver: (ctx, params) =>
    makeSessionFileUploadResolver<FileHubBaseMetadata>(ctx, {
      productCode: params.productCode,
      kind: SessionFileKind.DOCUMENT,
      tag,
    }),
  makeDeleteHandler: (ctx) => makeSessionFileDeleteHandler<FileHubBaseMetadata>(ctx),
  project: projectSingleDoc,
})

// ─── Kind descriptors ────────────────────────────────────────────────────────
// Scope strings + doc tags are VERBATIM — the BE keys uploads on them.

export const CERT_EXTERIOR_VIEW_KIND = docKind('EXTERIOR_VIEW', UploadTagType.EXTERNAL_VIEW)
export const CERT_HEATER_NAMEPLATE_KIND = docKind('HEATER_NAMEPLATE', UploadTagType.HEATING_SYSTEM_LABEL)
export const CERT_CONSTRUCTION_DESC_KIND = docKind('CONSTRUCTION_DESCRIPTION', UploadTagType.CONSTRUCTION_DESCRIPTION)
export const CERT_BUILDING_SHAPE_KIND = docKind('ENERGY_CERTIFICATE_BUILDING_SHAPE', UploadTagType.GROUND_PLAN)

// ─── Provider + hook pairs (one per scope) ─────────────────────────────────────

const [ExteriorViewProvider, useExteriorViewUpload] = createFileHubKind(CERT_EXTERIOR_VIEW_KIND)
const [HeaterNameplateProvider, useHeaterNameplateUpload] = createFileHubKind(CERT_HEATER_NAMEPLATE_KIND)
const [ConstructionDescProvider, useConstructionDescUpload] = createFileHubKind(CERT_CONSTRUCTION_DESC_KIND)
const [BuildingShapeProvider, useBuildingShapeUpload] = createFileHubKind(CERT_BUILDING_SHAPE_KIND)

export { useExteriorViewUpload, useHeaterNameplateUpload, useConstructionDescUpload, useBuildingShapeUpload }

/**
 * The single owner of all four cert FileAPI scopes. Mounted ONCE at the PF-v2
 * root (`PurchaseFlowRevamp.page`, wrapping `<Routes>`) next to
 * `PfV2VisualUploadProvider` + `PfV2PoaUploadProvider`, so the scopes survive
 * step/tab switches; the default `purgeOnDestroy` clears them on leaving
 * `/purchase-flow-revamp/*`. Composed by nesting the four scope providers.
 */
export const PfV2AdditionalInfoUploadProvider: FC<{ children: ReactNode }> = ({ children }) =>
  [ExteriorViewProvider, HeaterNameplateProvider, ConstructionDescProvider, BuildingShapeProvider].reduceRight<ReactElement>(
    (acc, Provider) => createElement(Provider, null, acc),
    children as ReactElement
  )

/** A certificate scope as one cart line sees it: only the files that line uploaded or resumed. */
export interface LineDocScope {
  entries: ReturnType<typeof useExteriorViewUpload>['entries']
  /** The line's stored filename, at most one, as `projectSingleDoc` reads a scope. */
  filenames: string[]
  upload: (files: File[]) => void
  remove: (id: string) => void
  seed: (filenames: string[]) => void
  /** Whether the client has uploaded or deleted in this line's area since the session began. */
  touched: boolean
  markTouched: () => void
}

/**
 * Narrows a shared certificate scope to one cart line by the tag each upload carries. Without it a
 * consumption and an energy certificate in one cart recorded, showed and deleted each other's photos.
 */
export function useLineDocScope(
  hub: ReturnType<typeof useExteriorViewUpload>,
  cartItemId: string,
  productCode: string | null
): LineDocScope {
  const entries = useMemo(() => hub.entries.filter((entry) => entry.tag === cartItemId), [hub.entries, cartItemId])
  const filenames = useMemo(() => projectSingleDoc(entries).filenames, [entries])
  const { upload: hubUpload, seed: hubSeed, remove, touchedTags, markTouched: hubMarkTouched } = hub
  const upload = useCallback((files: File[]) => hubUpload(files, { productCode, cartItemId }), [hubUpload, productCode, cartItemId])
  const seed = useCallback((names: string[]) => hubSeed(names, cartItemId), [hubSeed, cartItemId])
  const markTouched = useCallback(() => hubMarkTouched(cartItemId), [hubMarkTouched, cartItemId])
  return { entries, filenames, upload, remove, seed, touched: touchedTags.has(cartItemId), markTouched }
}
